import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}
interface State {
  hasError: boolean
}

/**
 * Catches render/lifecycle exceptions so one broken screen degrades to a recoverable message
 * instead of white-screening the whole SPA. Wraps the app at the root.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to the console for diagnosis; a real error sink can hook in here later.
    console.error('Unhandled UI error:', error, info.componentStack)
  }

  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-lg font-semibold text-brand-darkest">Something went wrong</h1>
        <p className="text-sm text-gray-500 max-w-sm">
          The page hit an unexpected error. Reloading usually fixes it — your data is safe.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark"
        >
          Reload
        </button>
      </div>
    )
  }
}
